"use client";

import { useEffect, useState } from "react";

import styles from "./_faq-nav.module.css";

/**
 * Guide `.faq-nav` — the "Jump to" rail, with the ACTIVE category marked.
 *
 * It was a plain list of links with no current state, so a reader three
 * categories deep had nothing telling them where they were — which is most of
 * what a jump rail is for.
 *
 * A client island only because "which panel am I in" cannot be known on the
 * server. The panels themselves stay server-rendered; this ships one observer.
 */
export function FaqNav({
  items,
  label,
}: {
  items: { id: string; title: string; count: number }[];
  label: string;
}) {
  const [active, setActive] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const panels = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el != null);
    if (panels.length === 0) return undefined;

    // The band runs from just under the sticky chrome to a little past the
    // middle, so the "current" panel is the one the reader is actually looking
    // at rather than whichever happens to touch the viewport edge. Recomputed
    // from live geometry on every callback for the same reason the tab bar
    // does it: the answer must not depend on which entries changed.
    const pick = (): void => {
      const marker = 140;
      let current: string | null = null;
      for (const panel of panels) {
        if (panel.getBoundingClientRect().top <= marker) current = panel.id;
      }
      setActive(current ?? panels[0]!.id);
    };

    const observer = new IntersectionObserver(pick, {
      rootMargin: "-140px 0px -55% 0px",
      threshold: 0,
    });
    panels.forEach((panel) => observer.observe(panel));
    pick();
    return () => observer.disconnect();
  }, [items]);

  return (
    <nav aria-label={label} className={styles.nav}>
      <div className={styles.label}>{label}</div>
      {items.map((item) => {
        const isActive = item.id === active;
        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            aria-current={isActive ? "true" : undefined}
            className={styles.item}
          >
            <span className={styles.title}>{item.title}</span>
            <span className={styles.count}>{item.count}</span>
          </a>
        );
      })}
    </nav>
  );
}
